// © 2026 Camp Promethean, LLC. All Rights Reserved.
// Remembers where a tier-locked user was headed so the post-upgrade success
// page can send them back. sessionStorage: survives the Stripe Checkout
// round-trip in the same tab, gone when the tab closes.

import type { MembershipTier } from "@/types/navigation";

export interface UpgradeIntent {
  path: string;
  requiredTier: MembershipTier;
  capturedAt: string;
}

const STORAGE_KEY = "cp.upgradeIntent";
const MAX_AGE_MS = 60 * 60 * 1000; // stale after an hour

export function saveUpgradeIntent(path: string, requiredTier: MembershipTier) {
  try {
    const intent: UpgradeIntent = {
      path,
      requiredTier,
      capturedAt: new Date().toISOString(),
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(intent));
  } catch {
    // storage unavailable (private mode etc.) — intent is best-effort
  }
}

export function readUpgradeIntent(): UpgradeIntent | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const intent = JSON.parse(raw) as UpgradeIntent;
    if (!intent?.path || !intent?.requiredTier) return null;
    if (Date.now() - new Date(intent.capturedAt).getTime() > MAX_AGE_MS) {
      clearUpgradeIntent();
      return null;
    }
    return intent;
  } catch {
    return null;
  }
}

export function clearUpgradeIntent() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
